import { PostValidationErrorMessage } from '~publish/legacy/constants'
import { getDateString } from '~publish/legacy/duplicate-server/formatters'

import type { PublishingPost } from './graphqlTypes'
import { PostEntity } from './PostEntity'
import type {
  RpcUpdate,
  RpcUpdateDocument,
  RpcUpdateImage,
  RpcUpdateLinkMedia,
  RpcUpdateVideo,
} from './types'

export const getPostValidationMessage = ({
  post,
  userNeedsApproval,
}: {
  post: RpcUpdate | PublishingPost
  userNeedsApproval: boolean
}): string | undefined => {
  const validationError = PostEntity.validatePost(post)
  if (!validationError) return

  const baseMessageSuffix = `before you ${
    userNeedsApproval ? 'request approval for' : 'schedule'
  } this post`
  const errorMessage = PostValidationErrorMessage[validationError]
  if (!errorMessage) {
    return validationError
  }
  return `${errorMessage} ${baseMessageSuffix}`
}

const getPostActionStringForSentPost = (
  post: RpcUpdate | PublishingPost,
  profileTimezone: string | undefined,
  twentyFourHourTime: boolean,
): string | undefined => {
  const dueAt = PostEntity.getDueAt(post)
  // We don't have an effective way to determine if an old post is a reminder
  // For new ones, we can use is_reminder field. For the old ones, if they were sent, their status is 'notified (isPastReminder)'
  const isReminder = PostEntity.isPastReminder(post)
  const isRepin = PostEntity.isRepin(post)
  if (dueAt) {
    const dateString = getDateString(dueAt, profileTimezone, {
      twentyFourHourTime,
    })

    if (isReminder) {
      return dateString.startsWith('today')
        ? `Notification sent ${dateString}.`
        : `Notification sent on ${dateString}.`
    }
    if (post.via) {
      let via = post.via
      if (via === 'Twitter') {
        via = 'Twitter / X'
      }
      const viaDateString = getDateString(dueAt, profileTimezone, {
        twentyFourHourTime,
      })
      const verb = isRepin ? 'Pinned' : 'Shared'

      if (viaDateString.includes('today')) {
        return `${verb} via ${via} ${viaDateString}`
      } else {
        return `${verb} via ${via} on ${viaDateString}`
      }
    }
    return `Shared on ${dateString}`
  }
}

const getPostType = (isDraft: boolean, isReminder: boolean): string => {
  if (isReminder) {
    return 'Notification'
  }
  if (isDraft) {
    return 'Draft'
  }
  return 'Post'
}

const getPostActionStringForUnsentPost = (
  post: RpcUpdate | PublishingPost,
  profileTimezone: string | undefined,
  twentyFourHourTime: boolean,
  isReminder: boolean,
): string | undefined => {
  const isDraft = PostEntity.isDraft(post)
  const dueAt = PostEntity.getDueAt(post)

  if (dueAt) {
    const dateString = getDateString(dueAt, profileTimezone, {
      twentyFourHourTime,
    })
    const postType = getPostType(isDraft, isReminder)
    return `${postType} scheduled for ${dateString}.`
  }
}

interface GetPostActionStringInput {
  post: RpcUpdate | PublishingPost | undefined
  profileTimezone: string | undefined
  twentyFourHourTime: boolean
  isReminder: boolean
}
export const getPostActionString = ({
  post,
  profileTimezone,
  twentyFourHourTime,
  isReminder,
}: GetPostActionStringInput): string | undefined => {
  if (!post) {
    return
  }
  /* isSent emthod only can be used for posts, to know if a reminder was sent we have to use isPastReminder */
  if (PostEntity.isSent(post) || PostEntity.isPastReminder(post)) {
    return getPostActionStringForSentPost(
      post,
      profileTimezone,
      twentyFourHourTime,
    )
  } else {
    return getPostActionStringForUnsentPost(
      post,
      profileTimezone,
      twentyFourHourTime,
      isReminder,
    )
  }
}

export const isRpcUpdate = (post: unknown): post is RpcUpdate => {
  return (
    typeof post === 'object' &&
    post !== null &&
    'id' in post &&
    'status' in post &&
    'createdAt' in post &&
    ('profile_id' in post || 'profileId' in post)
  )
}

const getFileTypeFromPath = (path: string): string => {
  const fileNameParts = path.split('.')

  return fileNameParts.length > 1 ? fileNameParts[fileNameParts.length - 1] : ''
}

export const isRpcUpdateImageOrGif = (
  media: unknown,
): media is RpcUpdateImage => {
  return (
    typeof media === 'object' &&
    media !== null &&
    (('picture' in media && !!media.picture) ||
      ('photo' in media && !!media.photo)) &&
    !isRpcUpdateDocument(media) &&
    !isRpcUpdateVideo(media) &&
    !isRpcUpdateLinkMedia(media)
  )
}

export const isRpcUpdateImage = (media: unknown): media is RpcUpdateImage => {
  if (isRpcUpdateImageOrGif(media)) {
    const filePath = media?.picture ?? media?.photo
    return getFileTypeFromPath(filePath).toLowerCase() !== 'gif'
  }
  return false
}

export const isRpcUpdateGif = (media: unknown): media is RpcUpdateImage => {
  if (isRpcUpdateImageOrGif(media)) {
    const filePath = media?.picture ?? media?.photo
    return getFileTypeFromPath(filePath).toLowerCase() === 'gif'
  }
  return false
}

export const isRpcUpdateVideo = (media: unknown): media is RpcUpdateVideo => {
  return (
    typeof media === 'object' &&
    media !== null &&
    'video' in media &&
    !!media.video
  )
}

export const isRpcUpdateDocument = (
  media: unknown,
): media is RpcUpdateDocument => {
  return (
    typeof media === 'object' &&
    media !== null &&
    'document' in media &&
    !!media.document
  )
}

export const isRpcUpdateLinkMedia = (
  media: unknown,
): media is RpcUpdateLinkMedia => {
  return (
    typeof media === 'object' &&
    media !== null &&
    'link' in media &&
    !!media.link
  )
}
